/**
 * i18n — F-D2 는 "유지"였고, **파생 요구는 언어 전환기**다.
 *
 * 구 앱에는 locale 3종(ko/en/ja)·`t()` 52회가 있었지만 **전환기가 없었다**(`setLocale` 호출 0).
 * 즉 번역은 있는데 도달 경로가 없어 en/ja 는 사실상 사문화 상태였다. 그대로 옮기면 같은 상태를
 * React 에서 재생산하므로, 여기서는 전환기를 함께 만든다 — 그래야 "유지" 결정이 실질을 갖는다.
 *
 * 키 범위는 **구 앱과 같은 표면**(셸·인증·채팅·게이트·산출물 패널) + 이번에 생긴 표면
 * (작업함·파일함). 관리자 화면은 구 앱에서도 번역 대상이 아니었고(사내 운영자 전용) 여기서도 같다.
 */
import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'

import en from './locales/en.json'
import ja from './locales/ja.json'
import ko from './locales/ko.json'

export const LOCALES = [
  { code: 'ko', label: '한국어' },
  { code: 'en', label: 'English' },
  { code: 'ja', label: '日本語' },
] as const

export type LocaleCode = (typeof LOCALES)[number]['code']

const STORAGE_KEY = 'elaine.locale'

/** 저장된 선택 → 없으면 브라우저 언어 → 없으면 ko(사내 기본). */
export function initialLocale(): LocaleCode {
  const saved = typeof localStorage === 'undefined' ? null : localStorage.getItem(STORAGE_KEY)
  if (saved && LOCALES.some((l) => l.code === saved)) return saved as LocaleCode
  const browser = typeof navigator === 'undefined' ? '' : navigator.language.slice(0, 2)
  const matched = LOCALES.find((l) => l.code === browser)
  return matched?.code ?? 'ko'
}

export function setLocale(code: LocaleCode): void {
  localStorage.setItem(STORAGE_KEY, code)
  void i18n.changeLanguage(code)
  document.documentElement.lang = code
}

void i18n.use(initReactI18next).init({
  resources: {
    ko: { translation: ko },
    en: { translation: en },
    ja: { translation: ja },
  },
  lng: initialLocale(),
  // 번역이 빠진 키는 ko 로 떨어진다 — 빈 화면 대신 한국어라도 보이는 편이 낫다.
  fallbackLng: 'ko',
  interpolation: { escapeValue: false }, // React 가 이미 이스케이프한다
})

export default i18n
